import {skipToken} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {useListCustomerMessagesQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetInvoiceV4Query} from '@dms/api/finance';
import {customerRoutes, taskManagerRoutes} from '@dms/routes';

import {composePath} from 'shared/web';

import {CUSTOMER_INSIGHTS_REFRESH_ON_REOPEN_OPTIONS} from '../constants/CUSTOMER_INSIGHTS_REFRESH_ON_REOPEN_OPTIONS';
import {getCustomerInsightsGridConfigs} from '../utils/getCustomerInsightsGridConfigs';
import {getCustomerInsightsInvoiceHref} from '../utils/getCustomerInsightsInvoiceHref';
import {useCustomerInsightsEntitlements} from './useCustomerInsightsEntitlements';
import {
  type CustomerInsightsGridQueryResult,
  useCustomerInsightsGridQuery,
} from './useCustomerInsightsGridQuery';

interface UseCustomerInsightsQueriesArgs {
  customerId: string;
  enabled: boolean;
}

interface CustomerInsightsGridQueries {
  openInterests: CustomerInsightsGridQueryResult;
  openBusinessCases: CustomerInsightsGridQueryResult;
  unsuccessfulInterests: CustomerInsightsGridQueryResult;
  unsuccessfulBusinessCases: CustomerInsightsGridQueryResult;
  pastSales: CustomerInsightsGridQueryResult;
  pastPurchases: CustomerInsightsGridQueryResult;
  openServiceCases: CustomerInsightsGridQueryResult;
  serviceHistory: CustomerInsightsGridQueryResult;
  overdueInvoices: CustomerInsightsGridQueryResult;
  overdueTasks: CustomerInsightsGridQueryResult;
}

export interface CustomerInsightsQueries {
  entitlements: ReturnType<typeof useCustomerInsightsEntitlements>;
  customer: ReturnType<typeof useGetCustomerV2Query>;
  latestContact: ReturnType<typeof useListCustomerMessagesQuery>;
  grids: CustomerInsightsGridQueries;
  overdueInvoiceHref: string | undefined;
  overdueTaskHref: string | undefined;
  customerInvoicesHref: string;
  customerTasksHref: string;
  isRefreshing: boolean;
}

// The latest-contact policy filters delivered messages client-side. Keep the
// endpoint's bounded maximum so an undelivered newest message cannot hide the
// latest delivered one; a smaller limit would change that selection contract.
const LATEST_ELIGIBLE_MESSAGE_LIMIT = 20;

export function useCustomerInsightsQueries(
  args: UseCustomerInsightsQueriesArgs
): CustomerInsightsQueries {
  const entitlements = useCustomerInsightsEntitlements();
  const configs = getCustomerInsightsGridConfigs(args.customerId, undefined, {
    businessCaseSales: entitlements.businessCaseSales,
    businessCasePurchases: entitlements.businessCasePurchases,
  });
  const canQueryEntitledData = args.enabled && entitlements.status === 'ready';
  const customer = useGetCustomerV2Query(
    args.enabled ? {customerId: args.customerId} : skipToken,
    CUSTOMER_INSIGHTS_REFRESH_ON_REOPEN_OPTIONS
  );
  const latestContact = useListCustomerMessagesQuery(
    canQueryEntitledData && entitlements.communication
      ? {customerId: args.customerId, limit: LATEST_ELIGIBLE_MESSAGE_LIMIT}
      : skipToken,
    CUSTOMER_INSIGHTS_REFRESH_ON_REOPEN_OPTIONS
  );
  const openInterests = useCustomerInsightsGridQuery({
    ...configs.openInterests,
    enabled: canQueryEntitledData,
    entitled: entitlements.interests,
  });
  const openBusinessCases = useCustomerInsightsGridQuery({
    ...configs.openBusinessCases,
    enabled: canQueryEntitledData,
    entitled: entitlements.businessCases,
  });
  const unsuccessfulInterests = useCustomerInsightsGridQuery({
    ...configs.unsuccessfulInterests,
    enabled: canQueryEntitledData,
    entitled: entitlements.interests,
  });
  const unsuccessfulBusinessCases = useCustomerInsightsGridQuery({
    ...configs.unsuccessfulBusinessCases,
    enabled: canQueryEntitledData,
    entitled: entitlements.businessCases,
  });
  const pastSales = useCustomerInsightsGridQuery({
    ...configs.pastSales,
    enabled: canQueryEntitledData,
    entitled: entitlements.businessCaseSales,
  });
  const pastPurchases = useCustomerInsightsGridQuery({
    ...configs.pastPurchases,
    enabled: canQueryEntitledData,
    entitled: entitlements.businessCasePurchases,
  });
  const openServiceCases = useCustomerInsightsGridQuery({
    ...configs.openServiceCases,
    enabled: canQueryEntitledData,
    entitled: entitlements.serviceCases,
  });
  const serviceHistory = useCustomerInsightsGridQuery({
    ...configs.serviceHistory,
    enabled: canQueryEntitledData,
    entitled: entitlements.serviceCases,
  });
  const overdueInvoices = useCustomerInsightsGridQuery({
    ...configs.overdueInvoices,
    enabled: canQueryEntitledData,
    entitled: entitlements.invoices,
  });
  const overdueTasks = useCustomerInsightsGridQuery({
    ...configs.overdueTasks,
    enabled: canQueryEntitledData,
    entitled: entitlements.tasks,
  });
  const overdueInvoiceId = overdueInvoices.rows[0]?.id;
  const overdueInvoiceDetail = useGetInvoiceV4Query(
    canQueryEntitledData && entitlements.invoices && isNotNil(overdueInvoiceId)
      ? {invoiceId: overdueInvoiceId}
      : skipToken,
    CUSTOMER_INSIGHTS_REFRESH_ON_REOPEN_OPTIONS
  );
  const overdueInvoiceHref = match({
    overdueInvoiceId,
    invoiceDetail: overdueInvoiceDetail.currentData,
  })
    .with(
      {overdueInvoiceId: Pattern.string, invoiceDetail: Pattern.nonNullable},
      ({overdueInvoiceId: invoiceId, invoiceDetail}) =>
        getCustomerInsightsInvoiceHref({
          invoiceId,
          documentSource: invoiceDetail.documentSource,
          canReadBusinessCases: entitlements.businessCases,
          canReadServiceCases: entitlements.serviceCases,
        })
    )
    .otherwise(() => undefined);
  const overdueTaskId = overdueTasks.rows[0]?.id;
  const overdueTaskHref =
    overdueTaskId === undefined
      ? undefined
      : composePath(taskManagerRoutes.taskOverview, {
          queryParams: {taskId: overdueTaskId},
          isSearchParamsPreserved: false,
        });
  const customerInvoicesHref = composePath(customerRoutes.invoices, {
    params: {id: args.customerId},
  });
  const customerTasksHref = composePath(customerRoutes.tasks, {
    params: {id: args.customerId},
  });
  const gridQueries = [
    openInterests,
    openBusinessCases,
    unsuccessfulInterests,
    unsuccessfulBusinessCases,
    pastSales,
    pastPurchases,
    openServiceCases,
    serviceHistory,
    overdueInvoices,
    overdueTasks,
  ];
  const isRefreshing =
    gridQueries.some((query) => query.isFetching) ||
    (customer.isFetching && !customer.isLoading) ||
    (latestContact.isFetching && !latestContact.isLoading) ||
    (overdueInvoiceDetail.isFetching && !overdueInvoiceDetail.isLoading);

  return {
    entitlements,
    customer,
    latestContact,
    grids: {
      openInterests,
      openBusinessCases,
      unsuccessfulInterests,
      unsuccessfulBusinessCases,
      pastSales,
      pastPurchases,
      openServiceCases,
      serviceHistory,
      overdueInvoices,
      overdueTasks,
    },
    overdueInvoiceHref,
    overdueTaskHref,
    customerInvoicesHref,
    customerTasksHref,
    isRefreshing,
  };
}
